import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { ShopWerbungTabs } from "@/components/ShopWerbungTabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter,
} from "@/components/ui/sheet";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { cart, fmtEuro } from "@/lib/cart";
import { useSubscription } from "@/lib/useSubscription";
import { useAuth } from "@/lib/auth";
import FeatureLockedScreen from "@/components/FeatureLockedScreen";
import { AdminShopPanel } from "@/pages/AdminShop";
import {
  ShoppingBag, Loader2, Search, ShoppingCart, Trash2, Plus, Minus,
  Package, Sparkles, History, Settings, Store,
  // Icons used by products (resolved by string lookup)
  Shirt, ShieldCheck, FileText, Image as ImageIcon, CreditCard, FolderOpen,
  Pen, BookText, Coffee, Key, BatteryCharging, Usb, Briefcase, Flag,
  HardHat, ShieldAlert,
} from "lucide-react";

// Icon name → component map (because lucide-react has no dynamic-import API)
const ICON_MAP = {
  Shirt, Coat: ShieldCheck, HardHat, Vest: ShieldAlert, Flag, Image: ImageIcon, FileText,
  CreditCard, FolderOpen, Pen, BookText, Coffee, Key, BatteryCharging, Usb, Briefcase, Package,
};

const COLOR_MAP = {
  emerald: "from-emerald-500 to-cyan-500",
  cyan:    "from-cyan-500 to-blue-600",
  rose:    "from-rose-500 to-pink-600",
  amber:   "from-amber-500 to-orange-500",
  violet:  "from-violet-500 to-fuchsia-500",
};

const CATEGORIES = [
  { key: "all",          label: "Alle Produkte" },
  { key: "bekleidung",   label: "Bekleidung" },
  { key: "werbemittel",  label: "Werbemittel" },
  { key: "giveaways",    label: "Give-Aways" },
  { key: "buero",        label: "Büro" },
];

export default function Shop() {
  const { isActive, isAdmin } = useSubscription();
  if (!isActive && !isAdmin) {
    return (
      <Layout title="Shop">
        <div className="mb-5"><ShopWerbungTabs /></div>
        <FeatureLockedScreen
          icon={ShoppingBag}
          title="Shop wird mit deinem Abo freigeschaltet"
          subtitle="Bestelle professionelle Werbeartikel, Bekleidung und Give-Aways mit deinem Logo — sobald dein Plan aktiv ist."
          bullets={[
            "Visitenkarten, Flyer, Türschilder mit deinem Logo",
            "Hochwertige Polos, Caps & Jacken",
            "Sonderkonditionen für Bestandskunden",
            "Direktversand an deine Geschäftsadresse",
            "Vorlagen aus dem Werbematerial-Modul",
            "Verbuchung via SEPA-Lastschrift",
          ]}
          trigger="shop"
        />
      </Layout>
    );
  }
  return <ShopInner />;
}

function ShopInner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "backoffice";
  const [view, setView] = useState("catalog"); // catalog | admin (nur für isManager)
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [cartItems, setCartItems] = useState(cart.items);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    api.get("/shop/products").then(({ data }) => setProducts(data.items || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => cart.subscribe(setCartItems), []);

  const visible = products.filter((p) => {
    if (category !== "all" && p.category !== category) return false;
    if (q && !`${p.name} ${p.sku} ${p.description}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <Layout
      title="Shop"
      subtitle="Bekleidung, Werbemittel & Give-Aways direkt von Easy Gutachter"
      action={
        <div className="flex items-center gap-2">
          <Link to="/shop/orders">
            <Button variant="outline" size="sm" data-testid="shop-my-orders-btn">
              <History className="h-4 w-4 mr-2" /> Meine Bestellungen
            </Button>
          </Link>
          <CartButton
            items={cartItems}
            onOpen={() => setCartOpen(true)}
            cartOpen={cartOpen}
            onCheckout={() => { setCartOpen(false); navigate("/shop/checkout"); }}
            onClose={() => setCartOpen(false)}
          />
        </div>
      }
    >
      <div className="max-w-7xl space-y-6">
        <ShopWerbungTabs />
        {/* Admin/Backoffice: Tab-Switcher zwischen Katalog & Verwaltung */}
        {isManager && (
          <div className="flex items-center gap-1 p-1 bg-zinc-100 rounded-xl w-fit" data-testid="shop-view-tabs">
            <button
              type="button"
              onClick={() => setView("catalog")}
              data-testid="shop-tab-catalog"
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                view === "catalog"
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-600 hover:text-zinc-900"
              )}
            >
              <Store className="h-4 w-4" /> Katalog
            </button>
            <button
              type="button"
              onClick={() => setView("admin")}
              data-testid="shop-tab-admin"
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                view === "admin"
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-600 hover:text-zinc-900"
              )}
            >
              <Settings className="h-4 w-4" /> Verwaltung
            </button>
          </div>
        )}

        {isManager && view === "admin" ? (
          <AdminShopPanel />
        ) : (
        <>
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-emerald-500 to-cyan-500 text-white p-7" data-testid="shop-hero">
          <div className="absolute right-6 top-6 opacity-30"><ShoppingBag className="h-24 w-24" /></div>
          <Badge className="bg-white/20 backdrop-blur border-white/30 text-white mb-3">
            <Sparkles className="h-3 w-3 mr-1" /> Versandkostenfrei ab 100 €
          </Badge>
          <h2 className="font-display text-3xl font-bold tracking-tight max-w-2xl">
            Dein Auftritt — von Visitenkarte bis Softshell-Jacke.
          </h2>
          <p className="mt-2 text-white/90 max-w-xl">
            Hochwertige Bekleidung, Werbemittel und Give-Aways mit deinem Logo.
            Alle Produkte werden im Auftrag von Easy Gutachter veredelt und versendet.
          </p>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Produkt suchen…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10"
              data-testid="shop-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                data-testid={`shop-cat-${c.key}`}
                className={cn(
                  "text-[12px] font-semibold px-3 py-1.5 rounded-full border transition",
                  category === c.key
                    ? "bg-emerald-600 text-white border-emerald-600 shadow"
                    : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Product grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
        ) : visible.length === 0 ? (
          <Card className="p-12 text-center" data-testid="shop-empty">
            <div className="font-display text-lg font-bold mb-1">Keine Produkte gefunden</div>
            <p className="text-sm text-zinc-500">Versuche eine andere Kategorie oder Suche.</p>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="shop-grid">
            {visible.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
        </>
        )}
      </div>
    </Layout>
  );
}

function CartButton({ items, onOpen, cartOpen, onClose, onCheckout }) {
  const total = cart.subtotalCents();
  const count = cart.count();
  return (
    <Sheet open={cartOpen} onOpenChange={(o) => o ? onOpen() : onClose()}>
      <SheetTrigger asChild>
        <Button data-testid="shop-cart-btn">
          <ShoppingCart className="h-4 w-4 mr-2" />
          Warenkorb
          {count > 0 && (
            <span className="ml-2 inline-flex items-center justify-center bg-white text-emerald-700 font-bold text-[11px] h-5 min-w-5 px-1 rounded-full">
              {count}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex flex-col w-full sm:max-w-md" data-testid="shop-cart-drawer">
        <SheetHeader>
          <SheetTitle>Warenkorb · {count} Artikel</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-4">
          {items.length === 0 ? (
            <div className="text-center text-sm text-zinc-400 py-12">
              <ShoppingCart className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
              Noch keine Artikel im Warenkorb.
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((it) => <CartLine key={it.lineKey} item={it} />)}
            </ul>
          )}
        </div>
        {items.length > 0 && (
          <SheetFooter className="border-t border-zinc-100 pt-4 -mx-6 px-6 flex-col sm:flex-col items-stretch gap-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Zwischensumme (netto)</span>
              <span className="font-display font-bold tabular-nums">{fmtEuro(total)}</span>
            </div>
            <Button onClick={onCheckout} size="lg" data-testid="shop-cart-checkout">
              Zur Bestellung →
            </Button>
            <button
              type="button"
              onClick={() => cart.clear()}
              className="text-xs text-zinc-400 hover:text-rose-600 transition"
              data-testid="shop-cart-clear"
            >
              Warenkorb leeren
            </button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CartLine({ item }) {
  const Icon = ICON_MAP[item.icon] || Package;
  const tone = COLOR_MAP[item.color] || COLOR_MAP.emerald;
  return (
    <li className="flex gap-3" data-testid={`cart-line-${item.lineKey}`}>
      <div className={cn("h-14 w-14 rounded-xl flex items-center justify-center text-white shrink-0 bg-gradient-to-br", tone)}>
        <Icon className="h-6 w-6" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm truncate">{item.name}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          {[item.size && `Größe ${item.size}`, item.color_choice].filter(Boolean).join(" · ") || item.unit_label}
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <button onClick={() => cart.setQty(item.lineKey, item.qty - 1)} className="h-6 w-6 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center" aria-label="Weniger" data-testid={`cart-line-${item.lineKey}-minus`}>
            <Minus className="h-3 w-3" />
          </button>
          <span className="text-sm font-mono tabular-nums w-7 text-center">{item.qty}</span>
          <button onClick={() => cart.setQty(item.lineKey, item.qty + 1)} className="h-6 w-6 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center" aria-label="Mehr" data-testid={`cart-line-${item.lineKey}-plus`}>
            <Plus className="h-3 w-3" />
          </button>
          <span className="ml-auto text-sm font-display font-bold tabular-nums">
            {fmtEuro(item.unit_price_cents * item.qty)}
          </span>
          <button onClick={() => cart.remove(item.lineKey)} className="h-6 w-6 rounded-md text-rose-500 hover:bg-rose-50 flex items-center justify-center" aria-label="Entfernen" data-testid={`cart-line-${item.lineKey}-remove`}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </li>
  );
}

function ProductCard({ product }) {
  const Icon = ICON_MAP[product.icon] || Package;
  const tone = COLOR_MAP[product.color] || COLOR_MAP.emerald;
  const [size, setSize] = useState(product.sizes?.[0] || "");
  const [color, setColor] = useState(product.colors?.[0] || "");
  const [qty, setQty] = useState(product.min_qty || 1);

  const add = () => {
    if (product.sizes?.length && !size) return toast.error("Bitte Größe wählen");
    cart.add(product, { qty, size: size || null, color: color || null });
    toast.success(`${product.name} hinzugefügt`);
  };

  return (
    <Card className="overflow-hidden flex flex-col group hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid={`product-${product.sku}`}>
      <div className={cn("aspect-[4/3] flex items-center justify-center relative overflow-hidden bg-gradient-to-br text-white", tone)}>
        {product.image_path ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${product.image_path}?auth=${typeof localStorage !== "undefined" ? (localStorage.getItem("access_token") || "") : ""}`}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <Icon className="h-20 w-20 opacity-50 group-hover:scale-110 transition-transform" strokeWidth={1.4} />
        )}
        <Badge className="absolute top-3 left-3 bg-white/95 text-zinc-700 hover:bg-white text-[10px] font-bold uppercase tracking-wider">
          {product.sku}
        </Badge>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="font-display font-semibold tracking-tight">{product.name}</div>
        <p className="text-xs text-zinc-500 mt-1 line-clamp-2 flex-1">{product.description}</p>

        <div className="mt-3 space-y-2">
          {product.sizes?.length > 0 && (
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger className="h-9 text-sm" data-testid={`product-${product.sku}-size`}>
                <SelectValue placeholder="Größe wählen" />
              </SelectTrigger>
              <SelectContent>
                {product.sizes.map((s) => <SelectItem key={s} value={s}>Größe {s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {product.colors?.length > 0 && (
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger className="h-9 text-sm" data-testid={`product-${product.sku}-color`}>
                <SelectValue placeholder="Farbe wählen" />
              </SelectTrigger>
              <SelectContent>
                {product.colors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="font-display text-xl font-bold tabular-nums">{fmtEuro(product.price_cents)}</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-wider">pro {product.unit_label}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setQty(Math.max(product.min_qty || 1, qty - 1))}
              className="h-8 w-8 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center"
              aria-label="Weniger"
              data-testid={`product-${product.sku}-minus`}
            ><Minus className="h-3 w-3" /></button>
            <span className="text-sm font-mono tabular-nums w-7 text-center">{qty}</span>
            <button
              onClick={() => setQty(qty + 1)}
              className="h-8 w-8 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center"
              aria-label="Mehr"
              data-testid={`product-${product.sku}-plus`}
            ><Plus className="h-3 w-3" /></button>
          </div>
        </div>
        <Button onClick={add} className="mt-3 w-full" data-testid={`product-${product.sku}-add`}>
          <ShoppingCart className="h-4 w-4 mr-2" /> In den Warenkorb
        </Button>
      </div>
    </Card>
  );
}
